import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function TournamentDatesCard({ tournament, isLocked, onSaved }) {
    const [startDate, setStartDate] = useState(tournament.start_date || "");
    const [endDate, setEndDate] = useState(tournament.end_date || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setStartDate(tournament.start_date || "");
        setEndDate(tournament.end_date || "");
    }, [tournament.start_date, tournament.end_date]);

    const isDirty = (startDate || "") !== (tournament.start_date || "")
        || (endDate || "") !== (tournament.end_date || "");

    const save = async () => {
        setSaving(true);
        try {
            await api.updateTournamentDates(tournament.id, {
                start_date: startDate || null,
                end_date: endDate || null,
            });
            toast.success("Tournament dates saved");
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
        setSaving(false);
    };

    return (
        <div className="brutal-card p-5 space-y-3" data-testid="tournament-dates-card">
            <h2 className="font-display font-extrabold text-xl uppercase">Tournament window</h2>
            <p className="text-xs text-zinc-400 leading-relaxed">
                Set the start and end dates so ESPN sync only pulls fixtures inside this window.
                Leave blank to fall back to the default 105-day window (60 days back, 45 days forward).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="block text-[10px] uppercase text-zinc-500 font-bold mb-1">Start date</label>
                    <input
                        data-testid="tournament-start-date-input"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={isLocked}
                        className="brutal-input w-full disabled:opacity-40"
                    />
                </div>
                <div>
                    <label className="block text-[10px] uppercase text-zinc-500 font-bold mb-1">End date</label>
                    <input
                        data-testid="tournament-end-date-input"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={isLocked}
                        className="brutal-input w-full disabled:opacity-40"
                    />
                </div>
            </div>
            <div className="flex gap-2">
                <button
                    data-testid="tournament-save-dates-btn"
                    onClick={save}
                    disabled={saving || isLocked || !isDirty}
                    className="brutal-btn text-xs py-2 bg-blaze text-white border-black shadow-brutal disabled:opacity-40"
                >
                    <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save dates"}
                </button>
                {(startDate || endDate) && !isLocked && (
                    <button
                        data-testid="tournament-clear-dates-btn"
                        onClick={() => { setStartDate(""); setEndDate(""); }}
                        className="brutal-btn-ghost text-xs"
                    >
                        Clear
                    </button>
                )}
            </div>
        </div>
    );
}
